/**
 * PerChildMaterialsOrderPreview · iter303ak-fix27 — Accepted · T50 · Action 20
 * ──────────────────────────────────────────────────────────────────────────
 * Accordion — one iframe per (accepted child × supplier). Uses the
 * existing /api/appraisals/{child_id}/materials-order-pdf?supplier=<slug>
 * endpoint. Per-item "Include in send" toggle (default on).
 * Output written to captured_data:
 *   per_child_materials_order_preview: {
 *     items: [{ child_id, supplier_slug, supplier_name, appraisal_no, include, total, line_count }, …],
 *   }
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, FileText, ExternalLink, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Local slugify — mirror the backend's `routes.supplier_letterheads.slugify`.
// Used as a cache key + iframe URL param when the overview doesn't
// already include a slug.
const slugify = (s) => (s || '')
  .toString()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'unknown';

export default function PerChildMaterialsOrderPreview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [overviews, setOverviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [includeMap, setIncludeMap] = useState(() => value?.include_map || {});
  const [err, setErr] = useState('');

  const loadOverviews = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const results = {};
      for (const c of acceptedChildren) {
        try {
          const { data } = await api.get(`/appraisals/${c.child_id}/materials-overview`);
          results[c.child_id] = data;
        } catch (e) {
          results[c.child_id] = { error: e?.response?.data?.detail || 'Failed to load' };
        }
      }
      setOverviews(results);
    } finally { setLoading(false); }
  }, [acceptedChildren]);

  useEffect(() => { loadOverviews(); }, [loadOverviews]);

  const items = useMemo(() => {
    const out = [];
    for (const c of acceptedChildren) {
      const o = overviews[c.child_id] || {};
      const suppliers = o.suppliers || [];
      if (suppliers.length === 0) {
        out.push({
          child_id: c.child_id,
          appraisal_no: c.appraisal_no || o.appraisal_no,
          quote_type_ref: c.quote_type_ref,
          variant_label: c.variant_label,
          supplier_name: '',
          supplier_slug: '',
          empty: true,
          include: false,
        });
      } else {
        for (const s of suppliers) {
          const supSlug = s.slug || slugify(s.supplier);
          const key = `${c.child_id}|${supSlug}`;
          const defaultInclude = includeMap[key] === undefined ? true : includeMap[key];
          out.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no || o.appraisal_no,
            quote_type_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            supplier_name: s.supplier,
            supplier_slug: supSlug,
            total: s.total,
            line_count: (s.materials || []).length,
            include: defaultInclude,
          });
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedChildren, overviews]);

  // Auto-expand the first non-empty item on load.
  useEffect(() => {
    if (items.length > 0 && expandedKeys.size === 0) {
      const first = items.find(i => !i.empty);
      if (first) setExpandedKeys(new Set([`${first.child_id}|${first.supplier_slug}`]));
    }
  }, [items, expandedKeys]);

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleInclude = (child_id, supplier_slug) => {
    const key = `${child_id}|${supplier_slug}`;
    setIncludeMap(prev => ({ ...prev, [key]: prev[key] === undefined ? false : !prev[key] }));
  };

  useEffect(() => {
    if (onChange) {
      const activeItems = items.filter(i => !i.empty).map(i => ({
        child_id: i.child_id,
        supplier_slug: i.supplier_slug,
        supplier_name: i.supplier_name,
        appraisal_no: i.appraisal_no,
        quote_type_ref: i.quote_type_ref,
        variant_label: i.variant_label,
        total: i.total,
        line_count: i.line_count,
        include: includeMap[`${i.child_id}|${i.supplier_slug}`] === undefined ? true : includeMap[`${i.child_id}|${i.supplier_slug}`],
      }));
      onChange({ items: activeItems, include_map: includeMap });
    }
  }, [items, includeMap, onChange]);

  const pdfHref = (child_id, supplier_slug) => `${BACKEND_URL}/api/appraisals/${child_id}/materials-order-pdf?supplier=${encodeURIComponent(supplier_slug)}&inline=1`;
  const openFileHref = (child_id) => `${BACKEND_URL}/api/appraisals/${child_id}/materials-order-pdf-combined?inline=1`;

  const includedCount = items.filter(i => !i.empty && (includeMap[`${i.child_id}|${i.supplier_slug}`] === undefined ? true : includeMap[`${i.child_id}|${i.supplier_slug}`])).length;

  return (
    <div className="space-y-4" data-testid="wf-per-child-materials-order-preview">
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-blue-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">Preview Supplier Order PDFs</div>
            <div className="text-sm mt-1 text-blue-800">
              {params.prompt || 'One PDF per supplier per accepted quote. Expand each panel to preview. Untick any supplier you don\'t want to send.'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-blue-600 font-semibold">Selected</div>
            <div className="text-lg font-bold text-blue-900 leading-none">{includedCount}<span className="text-xs text-blue-500 font-normal"> / {items.filter(i => !i.empty).length}</span></div>
          </div>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading supplier orders…</div>}

      {!loading && items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found.
        </div>
      )}

      {/* Group items by child for the per-child ZIP button */}
      {!loading && items.length > 0 && (() => {
        const groupedByChild = {};
        for (const it of items) {
          if (!groupedByChild[it.child_id]) groupedByChild[it.child_id] = [];
          groupedByChild[it.child_id].push(it);
        }
        return (
          <div className="space-y-3">
            {Object.entries(groupedByChild).map(([childId, childItems]) => {
              const first = childItems[0];
              return (
                <div key={childId} className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                  <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{first.appraisal_no}
                      </span>
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {[first.quote_type_ref, first.variant_label].filter(Boolean).join(' · ')}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5">
                        {childItems.filter(x => !x.empty).length} supplier order(s)
                      </span>
                    </div>
                    {!first.empty && (
                      <a
                        href={openFileHref(childId)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-teal-700 bg-white border border-teal-300 hover:bg-teal-50 inline-flex items-center gap-1"
                      >
                        <FileText size={11} /> Open file
                      </a>
                    )}
                  </div>
                  {childItems.map((it) => {
                    const key = `${it.child_id}|${it.supplier_slug || 'empty'}`;
                    const isExpanded = expandedKeys.has(key);
                    const include = includeMap[`${it.child_id}|${it.supplier_slug}`] === undefined ? true : includeMap[`${it.child_id}|${it.supplier_slug}`];
                    if (it.empty) {
                      return (
                        <div key={key} className="p-3 border-t border-slate-200 bg-amber-50" data-testid={`wf-mat-preview-empty-${it.child_id}`}>
                          <div className="flex items-center gap-2 text-xs text-amber-900">
                            <AlertTriangle size={12} />
                            <span>No supplier-scoped materials on this quote. Open the Materials tab to add supplier codes, or skip in the next step.</span>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={key} className={`border-t border-slate-100 ${include ? 'bg-white' : 'bg-slate-50 opacity-70'}`} data-testid={`wf-mat-preview-row-${it.child_id}-${it.supplier_slug}`}>
                        <div className="flex items-center justify-between gap-3 p-3">
                          <button
                            type="button"
                            onClick={() => toggleExpand(key)}
                            className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-900 min-w-0 flex-1 text-left"
                          >
                            <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            <span className="truncate">{it.supplier_name}</span>
                            <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                              {it.line_count} line{it.line_count === 1 ? '' : 's'}
                            </span>
                            {it.total != null && (
                              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                                {nz(it.total)}
                              </span>
                            )}
                          </button>
                          <div className="flex items-center gap-3 shrink-0">
                            <a href={pdfHref(it.child_id, it.supplier_slug)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                              <ExternalLink size={11} /> New tab
                            </a>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                              <input
                                type="checkbox"
                                checked={include}
                                onChange={() => toggleInclude(it.child_id, it.supplier_slug)}
                                className="rounded"
                                data-testid={`wf-mat-preview-include-${it.child_id}-${it.supplier_slug}`}
                              />
                              <span className={`font-medium ${include ? 'text-emerald-700' : 'text-slate-500'}`}>Include in send</span>
                            </label>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-slate-100 p-2 bg-slate-50">
                            <iframe
                              src={pdfHref(it.child_id, it.supplier_slug)}
                              title={it.supplier_name}
                              className="w-full bg-white border border-slate-200 rounded"
                              style={{ height: '600px' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })()}

      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-mat-preview-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-mat-preview-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={includedCount === 0}
            className="px-4 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-mat-preview-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue to Send ({includedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
